import React, { useState, useEffect, useRef } from "react"
import { IPatient } from "../../../../core/services/models/Patient"
import {
  getGeneticCounsellorDetails,
  saveGeneticAssessment,
  saveGeneticManagementPlan
} from "../../../../core/services/api/patientClinicalService"
import { getCaseSummaryData, getTemplatesByType, getVitals } from "../../../../core/services/api/patientDiagnosisService"
import SummaryView from "../../../../components/SummaryView"
import ConsultantNotesView from "../../../../components/ConsultantNotesView"
import JSONRenderer from "../../../../components/JSONRenderer/JSONRenderer"
import PedigreeView from "./components/PedigreeView/PedigreeView"
import Toast from "../../../../components/Toast"
import "./GeneticCounsellor.scss"
import { useSelector } from "react-redux"
import { MocUserRole } from "../../../../core/services/models/Enums"
import { getManageStaffDetailsById } from "core/services/api"
import Print from "components/Print/Print"

const GeneticCounsellor: React.FunctionComponent<any> = (
  {
    patientInfo,
    recordDetails,
    appointmentDetails,
    isReadOnly,
    selectedIpdTab,
    staffType,
    smartSummaryDetails,
    fetchSmartSummary
  }
) => {

  const centerId = appointmentDetails?.centerId
  const consultantId = appointmentDetails?.consultantId
  const appointmentId = appointmentDetails?.id
  const appointmentType = appointmentDetails?.appointmentType
  const recordId = recordDetails?.id
  const patientId = appointmentDetails?.patientId

  const componentRef = useRef(null)

  const [patientDetail, updatePatientDetail] = React.useState<IPatient>(patientInfo)
  const [vitalsData, setVitalsData] = useState<any>()
  const [selectedTab, setSelectedTab] = useState("Smart Summary")
  const [assessmentData, setAssessmentData] = useState<any>({})
  const [managementPlanData, setManagementPlanData] = useState<any>({})
  const [socioHistoryData, setSocioHistoryData] = useState<any>({})
  const [pedigreeDocs, setPedigreeDocs] = useState<any>([])
  const [smartSummaryData, setSmartSummaryData] = useState<any>()
  const userRole = useSelector((state: any) => state?.user?.role)
  const userDetail = useSelector((state: any) => state?.user?.userDetail)
  const tabSection: string[] = [
    "Smart Summary",
    "Assessment",
    "Attachment",
    "Follow up visit",
    "Management Plan"
  ]
  const [geneticAssessment, setGeneticAssessment] = useState<any>({})
  const [geneticAssessmentPrint, setGeneticAssessmentPrint] = useState<any>({})
  const [geneticManagementPlanJSON, setGeneticManagementPlanJSON] = useState<any>({})
  const [isCompleted, setIsCompleted] = useState<any>(false)
  const [managementPlanDataSaved, setManagementPlanDataSaved] = useState<any>(false)
  const [resConsultantNoteData, setResConsultantNoteData] = useState<any>({})
  const [isConsultantNoteData, setIsConsultantNoteData] = useState(false)
  const [staffDetails, setStaffDetails] = useState<any>()
  const [isLoading, setIsLoading] = useState<boolean>(false)

  useEffect(() => {
    let tempJson: any = {}
    let tempJsonPrint: any = {}
    let tempManagementJson: any = {}
    getTemplatesByType("GENETIC_COUNSELLOR")
      .then((res: any) => {
        tempJson = res?.find((e: any) => e?.subType === "Assessment")?.data[0]?.json || {}
        tempJsonPrint = res?.find((e: any) => e?.subType === "Assessment Print")?.data[0]?.json || {}
        tempManagementJson = res?.find((e: any) => e?.subType === "Management Plan")?.data[0]?.json || {}
        if (isReadOnly) {
          //Assessment
          tempJson["pages"][0]["readOnly"] = true
          tempJson["pages"][1]["readOnly"] = true
          tempJson["pages"][2]["readOnly"] = true
          tempJson["pages"][3]["readOnly"] = true
          setGeneticAssessment(tempJson)
          //Assessment Print
          setGeneticAssessmentPrint(tempJsonPrint)
          //Management Plan
          tempManagementJson["pages"][0]["readOnly"] = true
          tempManagementJson["showNavigationButtons"] = "none"
          setGeneticManagementPlanJSON(tempManagementJson)
        } else {
          setGeneticAssessment(tempJson)
          setGeneticAssessmentPrint(tempJsonPrint)
          setGeneticManagementPlanJSON(tempManagementJson)
        }
      })
      .catch((err: any) => console.log(err?.message))
  }, [isReadOnly])

  useEffect(() => {
    if (userDetail?.id) {
      getManageStaffDetailsById(userDetail?.id)
        .then((res: any) => {
          setStaffDetails(res)
          const temp = { ...managementPlanData }
          if (res?.primaryRole === staffType || (userRole === MocUserRole.GENETIC_COUNSELLOR && selectedIpdTab === "Genetic Counsellor")) {
            temp["geneticCounsellorName"] = res?.name
            if (!temp["geneticCounsellorSignature"]) {
              temp["geneticCounsellorSignature"] = res?.signaturePicUrl
            }
          }
          setManagementPlanData({ ...temp })
        })
        .catch((err: any) => console.log(err?.message))
    }

    if (recordId) {
      getVitals(recordId)
        .then((res: any) => {
          setVitalsData(res?.data)
        })
        .catch((err: any) => console.log(err?.message))
    }
  }, [])

  useEffect(() => {
    if (recordId && selectedTab !== "Follow up visit") {
      // setIsLoading(true)
      getGeneticCounsellorDetails(recordId)
        .then((data: any) => {
          setAssessmentData(data?.assessment)
          if (data?.pedigree !== null) {
            setPedigreeDocs(data?.pedigree)
          }
          if (selectedTab === "Management Plan") {
            const temp = { ...managementPlanData, ...data?.managementPlan }
            if (data?.assessment) {
              if (Object.keys(data?.assessment)?.includes("todaysDate")) {
                temp["pretestDate"] = data?.assessment?.todaysDate
              }
              if (Object.keys(data?.assessment)?.includes("GCNo")) {
                temp["gcNo"] = data?.assessment?.GCNo
              }
            }
            if (!temp["geneticCounsellorName"]) {
              temp["geneticCounsellorName"] = staffDetails?.name
            }
            if (!temp["geneticCounsellorSignature"]) {
              temp["geneticCounsellorSignature"] = staffDetails?.signaturePicUrl
            }
            if (!temp["patientID"]) {
              temp["patientID"] = patientInfo?.mocId
            }
            setManagementPlanData({ ...temp })
          }
          setIsLoading(false)
        })
        .catch((err: any) => {
          console.log(err)
          if (selectedTab === "Management Plan") {
            const temp = { ...managementPlanData }
            if (staffDetails?.primaryRole === staffType || (userRole === MocUserRole.GENETIC_COUNSELLOR && selectedIpdTab === "Genetic Counsellor")) {
              if (!temp["geneticCounsellorName"]) {
                temp["geneticCounsellorName"] = staffDetails?.name
              }
              if (!temp["geneticCounsellorSignature"]) {
                temp["geneticCounsellorSignature"] = staffDetails?.signaturePicUrl
              }
            }
            if (!temp["patientID"]) {
              temp["patientID"] = patientInfo?.mocId
            }
            setManagementPlanData({ ...temp })
          } else if (selectedTab === "Assessment") {
            if (smartSummaryData) {
              updateAssessmentData()
            }
          }
          setIsLoading(false)
        })
    }
  }, [selectedTab])

  useEffect(() => {
    if (patientId) {
      getCaseSummaryData(patientId)
        .then((data: any) => {
          setSocioHistoryData({ ...data?.data?.socioHistory })
        })
        .catch((err: any) => {
          console.log(err)
        })
    }
  }, [patientId])

  useEffect(() => {
    setSmartSummaryData(smartSummaryDetails)
  }, [smartSummaryDetails])

  useEffect(() => {
    setTimeout(() => {
      const completeBtn = document.getElementById("sv-nav-complete")
      if (isReadOnly && completeBtn) {
        completeBtn.style.display = "none"
      }
    }, 2000)
  }, [selectedTab])

  const updateAssessmentData = () => {
    let diagnosisStr = ""
    smartSummaryData?.diagnosis?.forEach((each: any) => {
      diagnosisStr += `${each?.diagnosis ? each?.diagnosis : "NA"} (${each?.cancerType ? each?.cancerType : ""}). `
    })
    let histopathologyStr = ""
    smartSummaryData?.histopathology?.forEach((each: any, index: any) => {
      histopathologyStr += `${each?.value ? each?.value : ""} (${smartSummaryData?.diagnosis[index]?.cancerType ? smartSummaryData?.diagnosis[index]?.cancerType : ""}). `
    })
    let pastTreatmentStr = ""
    smartSummaryData?.pastTreatment?.forEach((m: any, index: any) => {
      const res = getPastTreatment(index)
      pastTreatmentStr += `${res}. `
    })
    const temp = { ...assessmentData }
    if (!temp["Histopathology"]) {
      temp["Histopathology"] = histopathologyStr
    }
    if (!temp["diagnosis"]) {
      temp["diagnosis"] = diagnosisStr
    }
    if (!temp["treatmentUndertaken"]) {
      temp["treatmentUndertaken"] = pastTreatmentStr
    }
    setAssessmentData(temp)
  }

  const handleGeneticAssessmentCompletion = (data: any) => {
    // setIsLoading(true)
    saveGeneticAssessment(
      data,
      {
        centerId,
        consultantId,
        patientId,
        appointmentId,
        recordId,
      }
    )
      .then((res: any) => {
        setAssessmentData(res?.assessment)
        setIsLoading(false)
        setIsCompleted(true)
      })
      .catch((err: any) => {
        console.log(err)
        setIsLoading(false)
      })
  }

  const updateDocs = (docs: any) => {
    setPedigreeDocs(docs)
  }

  const getPastTreatment = (index: any) => {
    let pastTreatmentMessage = ""
    if (smartSummaryData?.pastTreatment === null) {
      return "NA"
    } else if (Object.keys(smartSummaryData?.pastTreatment[index]).length > 0) {
      if (index > 0) {
        pastTreatmentMessage += `\n${index + 1}) `
      } else {
        pastTreatmentMessage += `${index + 1}) `
      }
      let data: any = Object.keys(smartSummaryData?.pastTreatment[index])
      if (data?.includes("treatmentIntent") && smartSummaryData?.pastTreatment[index]["treatmentIntent"]) {
        pastTreatmentMessage += `Treatment Intent : ${smartSummaryData?.pastTreatment[index]["treatmentIntent"]}`
      }
      if (data?.includes("Type") && smartSummaryData?.pastTreatment[index]["Type"]) {
        pastTreatmentMessage += `,  Treatment Type : ${smartSummaryData?.pastTreatment[index]["Type"]}`
      }
      if ((data?.includes("from") && smartSummaryData?.pastTreatment[index]["from"]) &&
        (data?.includes("to") && smartSummaryData?.pastTreatment[index]["to"])) {
        pastTreatmentMessage += `,  From : ${smartSummaryData?.pastTreatment[index]["from"]} To : ${smartSummaryData?.pastTreatment[index]["to"]}`
      }
      if (data?.includes("details") && smartSummaryData?.pastTreatment[index]["details"]) {
        pastTreatmentMessage += `,  Details : ${smartSummaryData?.pastTreatment[index]["details"]}`
      }

      if (data?.includes("withTargetLesions") && smartSummaryData?.pastTreatment[index]["withTargetLesions"]) {
        pastTreatmentMessage += ".\nWith Target Lesions Response - "
        if (smartSummaryData?.pastTreatment[index]["withTargetLesions"][0]) {
          if (smartSummaryData?.pastTreatment[index]["withTargetLesions"][0]["targetLesionsResponse"]) {
            pastTreatmentMessage += `Target Lesions Response : ${smartSummaryData?.pastTreatment[index]["withTargetLesions"][0]["targetLesionsResponse"]}`
          }
          if (smartSummaryData?.pastTreatment[index]["withTargetLesions"][0]["nonTargetLesionsResponse"]) {
            pastTreatmentMessage += `, Non Target Lesions Response : ${smartSummaryData?.pastTreatment[index]["withTargetLesions"][0]["nonTargetLesionsResponse"]}`
          }
          if (smartSummaryData?.pastTreatment[index]["withTargetLesions"][0]["newLesions"]) {
            pastTreatmentMessage += `, New Lesions : ${smartSummaryData?.pastTreatment[index]["withTargetLesions"][0]["newLesions"]}`
          }
          if (smartSummaryData?.pastTreatment[index]["withTargetLesions"][0]["overallResponse"]) {
            pastTreatmentMessage += `, Overall Response : ${smartSummaryData?.pastTreatment[index]["withTargetLesions"][0]["overallResponse"]}`
          }
        } else {
          pastTreatmentMessage += "NA"
        }
      } else if (data?.includes("withoutTargetLesions") && smartSummaryData?.pastTreatment[index]["withoutTargetLesions"]) {
        pastTreatmentMessage += ".\nWithout Target Lesions Response - "
        if (smartSummaryData?.pastTreatment[index]["withoutTargetLesions"][0]) {
          if (smartSummaryData?.pastTreatment[index]["withoutTargetLesions"][0]["nonTargetLesionsResponse"]) {
            pastTreatmentMessage += `Non Target Lesions Response : ${smartSummaryData?.pastTreatment[index]["withoutTargetLesions"]["nonTargetLesionsResponse"]}`
          }
          if (smartSummaryData?.pastTreatment[index]["withoutTargetLesions"][0]["newLesions"]) {
            pastTreatmentMessage += `, New Lesions : ${smartSummaryData?.pastTreatment[index]["withoutTargetLesions"][0]["newLesions"]}`
          }
          if (smartSummaryData?.pastTreatment[index]["withoutTargetLesions"][0]["overallResponse"]) {
            pastTreatmentMessage += `, Overall Response : ${smartSummaryData?.pastTreatment[index]["withoutTargetLesions"][0]["overallResponse"]}`
          }
        } else {
          pastTreatmentMessage += "NA"
        }
      }
    } else {
      return "NA"
    }
    return pastTreatmentMessage
  }

  const handleManagementPlanCompltion = (data: any) => {
    // setIsLoading(true)
    saveGeneticManagementPlan(
      data,
      {
        centerId,
        consultantId,
        patientId,
        appointmentId,
        recordId,
      }
    )
      .then((res: any) => {
        setManagementPlanData(res?.managementPlan)
        setIsLoading(false)
        setManagementPlanDataSaved(true)
      })
      .catch((err: any) => {
        console.log(err)
        setIsLoading(false)
      })
  }

  return (
    <div className="genetic-page-container">
      {
        isLoading && (
          <div
            style={{
              position: "absolute",
              top: "50%",
              right: "50%",
            }}
          >
            <div
              style={{ width: "3rem", height: "3rem" }}
              className="spinner-border text-primary"
            ></div>
          </div>
        )
      }
      <div className="patient-diagnosis">
        <div className="patient-smart-summary">
          <div className="tab-wrapper">
            {tabSection.map((tabname) => (
              <div
                className={`tab ${selectedTab === tabname ? "active" : ""}`}
                onClick={() => {
                  setSelectedTab(tabname)
                  if (tabname === "Smart Summary") {
                    fetchSmartSummary()
                  }
                }}
              >
                {tabname}
              </div>
            ))}
          </div>
          <div className="content-wrapper">
            {selectedTab === "Smart Summary" ? (
              <SummaryView
                patientDetail={patientInfo}
                smartSummaryDetails={smartSummaryDetails}
              />
            ) : selectedTab === "Follow up visit" ? (
              <ConsultantNotesView
                setResConsultantNoteData={setResConsultantNoteData}
                setIsConsultantNoteData={setIsConsultantNoteData}
                recordDetails={recordDetails}
                appointmentDetails={appointmentDetails}
                patientDetail={patientInfo}
                isReadOnly={isReadOnly}
                selectedIpdTab={selectedIpdTab}
                staffType={staffType}
                smartSummaryData={smartSummaryData}
              />
            ) : selectedTab === "Assessment" ? (
              <div>
                <span
                  className="print-button cursor-pointer"
                  style={{ display: "flex", justifyContent: "flex-end" }}
                >
                  <Print
                    componentRef={componentRef}
                  />
                </span>
                <JSONRenderer
                  // printRef={componentRef}
                  json={geneticAssessment}
                  appointmentDate={appointmentDetails?.scheduleDate}
                  printJson={geneticAssessmentPrint}
                  data={assessmentData}
                  printData={assessmentData}
                  handleCompletion={handleGeneticAssessmentCompletion}
                  printRef={componentRef}
                  patientData={{ patientInfo: patientInfo, vitalsData: vitalsData }}
                  showPatientDetails={true}
                  origin={MocUserRole.GENETIC_COUNSELLOR}
                  smartSummaryData={smartSummaryData}
                />
              </div>
            ) : selectedTab === "Management Plan" ? (
              <div>
                <span
                  className="print-button cursor-pointer"
                  style={{ display: "flex", justifyContent: "flex-end" }}
                >
                  <Print
                    componentRef={componentRef}
                  />
                </span>
                <div className="management-plan-details">
                  <JSONRenderer
                    json={geneticManagementPlanJSON}
                    appointmentDate={appointmentDetails?.scheduleDate}
                    printJson={JSON.parse(JSON.stringify(geneticManagementPlanJSON))}
                    data={managementPlanData}
                    printData={managementPlanData}
                    patientData={{ patientInfo: patientInfo, vitalsData: vitalsData }}
                    handleCompletion={handleManagementPlanCompltion}
                    printRef={componentRef}
                    showPatientDetails={true}
                    origin={MocUserRole.GENETIC_COUNSELLOR}
                    smartSummaryData={smartSummaryData}
                  />
                </div>
              </div>
            ) : (
              <PedigreeView
                docs={pedigreeDocs}
                updateDocs={updateDocs}
                appointmentId={appointmentId}
                recordId={recordId}
                type="pedigree"
                isReadOnly={isReadOnly}
              />
            )

            }
          </div>
        </div>
        {
          isCompleted ? (
            <Toast
              textMessage="Assessment data saved successfully"
              onCloseText={"Close"}
              onClose={() => {
                setIsCompleted(false)
                const temp = { ...geneticAssessment }
                setGeneticAssessment({})
                setTimeout(() => {
                  setGeneticAssessment(temp)
                }, 0)
              }}
              type="success"
            ></Toast>
          ) : resConsultantNoteData?.id && isConsultantNoteData ? (
            <Toast
              textMessage={` Follow Up Data added Successfully`}
              onCloseText={"Close"}
              onClose={() => setIsConsultantNoteData(false)}
              type="success"
            ></Toast>
          ) : managementPlanDataSaved ? (
            <Toast
              textMessage="Management plan data saved successfully"
              onCloseText={"Close"}
              onClose={() => {
                setManagementPlanDataSaved(false)
                const temp = { ...geneticManagementPlanJSON }
                setGeneticManagementPlanJSON({})
                setTimeout(() => {
                  setGeneticManagementPlanJSON(temp)
                }, 0)
              }}
              type="success"
            ></Toast>
          ) : (
            ""
          )
        }
      </div>
    </div>
  )
}

export default GeneticCounsellor
